import { useCallback, useEffect } from 'react';

// eslint-disable-next-line import/no-extraneous-dependencies -- '@cornerstonejs/core' should be listed in the project's dependencies. Run 'npm i -S @cornerstonejs/core' to add it
import {
  type Types,
  getEnabledElement,
  getRenderingEngine,
} from '@cornerstonejs/core';
import styled from '@emotion/styled';
import { Close } from '@mui/icons-material';
import { CircularProgress, IconButton } from '@mui/material';
import classNames from 'classnames';
import { useAtom, useAtomValue } from 'jotai';
import { useResizeDetector } from 'react-resize-detector';
import { useHistory, useLocation, useParams } from 'react-router';

import { SegmentationsHatchPatterns } from '../../../common-components/elements/icon/SegmentationsHatchPatterns';
import { usePreferences } from '../../../react-query/usePreferences';
import { useGetImageWithSegmentations } from '../../../react-query/useSegmentations';
import { annotationStyles } from '../../../theme/annotationStyles';
import {
  blueBlack700,
  blueUltramarine100,
  blueUltramarine200,
} from '../../../theme/videa-style-tokens';
import { renderingEngineAtom } from '../../../utils/cornerstone/cornerstoneAtoms';
import { RENDERING_ENGINE } from '../../../utils/cornerstone/initializeCornerstone';
import { useCornerstone } from '../../../utils/cornerstone/useCornerstone';
import { useDicomCanvas } from '../../../utils/cornerstone/useDicomCanvas';
import { mixpanelEvents, useTrackEvent } from '../../../utils/mixpanel';
import { selectTeethAtom } from '../../patientList/atoms';
import { computeCursorClass } from '../annotation-utils/annotation-utils';
import { toothPartsTogglesAtom } from '../findingSidebar/atoms';
import { imageBrowserOpenAtom } from '../multiFileViewer/findingViewer/imageBrowser/atoms';
import {
  isHQEnabledAtom,
  showSegmentationAtom,
} from '../multiFileViewer/imageToolbar/atoms';
import type { ImageObject } from '../types';

import { activeImageIsSelectedAtom, annotateActionAtom } from './atoms';
import { useGetAnnotationsNearTools } from './helpers';

const StyleWrapper = styled.div`
  z-index: 0;
  margin: 1em 2em 0 2em;
  &.selected-photo {
    outline: 0.125em solid ${blueBlack700};
    &.selected {
      outline: 0.175em solid ${blueUltramarine200};
    }
    .close {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 1em;
      height: 1em;
      background-color: ${blueUltramarine200};
      position: absolute;
      z-index: 100;
      top: -0.5em;
      right: -0.5em;
      .close-icon {
        opacity: 1;
        width: 0.625em;
        fill: ${blueBlack700};
      }
    }
  }
  &.selected {
    outline: 0.175em solid ${blueUltramarine100};
  }
  .loadingSpinner {
    z-index: 5;
    position: absolute;
  }
  .errorIcon {
    color: red;
    position: absolute;
    z-index: 100;
  }
  .default {
    cursor: pointer !important;
    &.user-annotation {
      cursor: crosshair !important;
    }
  }
  &.cornerstoneContainer {
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100%;
    width: 100%;
    @media (max-width: 600px) {
      height: 100%;
      margin: 0;
    }
  }
  ${annotationStyles}
`;

export const CasePresentationImageViewer = ({
  activeImage,
  viewerMode,
  isSelectedPhoto,
}: {
  activeImage: ImageObject;
  viewerMode: number;
  isSelectedPhoto?: boolean;
}) => {
  const isImageWithSegmentations =
    useGetImageWithSegmentations(activeImage?.imageId) &&
    activeImage.detectedImageType !== 'COLOR_IMAGE';

  const isToothParts = useAtomValue(toothPartsTogglesAtom).tooth_anatomy;
  const showSegmentation = useAtomValue(showSegmentationAtom);
  const { data: prefs } = usePreferences();
  const isImageBrowserOpen = useAtomValue(imageBrowserOpenAtom);
  const selectedTeeth = useAtomValue(selectTeethAtom);
  const isHQEnabled = useAtomValue(isHQEnabledAtom);
  const [isActiveImageSelected, setIsActiveImageSelected] = useAtom(
    activeImageIsSelectedAtom,
  );

  const { trackEvent } = useTrackEvent();

  const { id: patientId } = useParams<{ id: string }>();

  const history = useHistory();
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const [dicomCanvas, dicomCanvasRef] = useDicomCanvas();
  const selectedPhotoId = params.get('selected-photo');

  const { loadImageFileIntoCanvas, imageLoadStatus, isCornerstoneReady } =
    useCornerstone({
      dicomCanvasRef: dicomCanvas,
      isThumbnail: false,
      activeImage,
      viewportId: isSelectedPhoto
        ? 'case-presentation-selected-photo'
        : 'case-presentation',
      source: 'case-presentation',
      selectedTeeth,
    });

  useGetAnnotationsNearTools(dicomCanvas, activeImage, isCornerstoneReady);

  useEffect(() => {
    if (activeImage && dicomCanvas && isCornerstoneReady) {
      loadImageFileIntoCanvas(isHQEnabled);
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps -- React Hook useEffect has a missing dependency: 'loadImageFileIntoCanvas'. Either include it or remove the dependency array. */
  }, [
    activeImage,
    dicomCanvas,
    isCornerstoneReady,
    isImageBrowserOpen,
    selectedPhotoId,
  ]);

  useEffect(() => {
    // Track how long we spend viewing each image
    const imageViewTime = performance.now();

    return () => {
      const imageExitTime = performance.now();
      const secondsElapsed = Math.round(imageExitTime - imageViewTime) / 1000;
      trackEvent(mixpanelEvents.casePresentationViewDuration, {
        duration: secondsElapsed,
        sideBySidePhoto: isSelectedPhoto,
        fileName: activeImage.fileName,
        patientId: activeImage.patientInfo.id,
        detectedImageType: activeImage.detectedImageType,
        estimatedVisitId: activeImage.estimatedVisitId,
        patient: patientId,
        image: activeImage.imageId,
      });
    };

    /* eslint-disable-next-line react-hooks/exhaustive-deps -- React Hook useEffect has missing dependencies: 'activeImage', 'isThumbnail', 'patient.id', 'trackEvent', and 'trackImageView'. Either include them or remove the dependency array. */
  }, [activeImage]);

  const isRenderingEngineInitialized = useAtomValue(renderingEngineAtom);
  const renderingEngine =
    isRenderingEngineInitialized && getRenderingEngine(RENDERING_ENGINE);

  const onResize = useCallback(() => {
    const viewportForImage =
      renderingEngine &&
      (renderingEngine.getViewport('case-presentation') as
        | Types.IStackViewport
        | undefined);
    try {
      if (
        activeImage &&
        dicomCanvas &&
        renderingEngine &&
        getEnabledElement(dicomCanvas as HTMLDivElement) &&
        viewportForImage &&
        viewportForImage.getCurrentImageId() &&
        viewportForImage.getImageData()
      ) {
        renderingEngine.resize();
      }
    } catch (error) {
      // Catch cornerstone error here. Hacky solution until we can get cornerstone core cloned locally and resolve type errors
    }
  }, [activeImage, dicomCanvas, renderingEngine]);

  const { ref: containerRef } = useResizeDetector({
    handleHeight: true,
    handleWidth: true,
    skipOnMount: true,
    refreshMode: undefined,
    onResize,
  });

  useEffect(() => {
    onResize();
  }, [selectedPhotoId, onResize]);

  const cursorClass = computeCursorClass(viewerMode);
  const annotateAction = useAtomValue(annotateActionAtom);

  const isImageSelected = isSelectedPhoto
    ? !isActiveImageSelected
    : isActiveImageSelected;

  const showToothParts =
    showSegmentation && isImageWithSegmentations && isToothParts;

  const cornerstoneClasses = classNames({
    ['cornerstoneElement']: true,
    [cursorClass]: true,
    'patient-view': showSegmentation && isImageWithSegmentations,
    'tooth-parts': showToothParts,
    'gray-pulp-segmentation':
      showToothParts && !!prefs?.enableGrayPulpSegmentation,
    'user-annotation':
      annotateAction === 'arrow' ||
      annotateAction === 'circle' ||
      annotateAction === 'segment',
  });

  return (
    <StyleWrapper
      className={classNames('cornerstoneContainer', {
        'selected-photo': isSelectedPhoto,
        selected: isImageSelected && !!selectedPhotoId,
      })}
      ref={containerRef}
      onClick={() => setIsActiveImageSelected(!isSelectedPhoto)}
    >
      {isSelectedPhoto && (
        <IconButton
          className="close"
          onClick={() => {
            params.delete('selected-photo');
            history.replace({ search: params.toString() });
          }}
        >
          <Close className="close-icon" />
        </IconButton>
      )}
      <SegmentationsHatchPatterns />
      {imageLoadStatus === 'loading' && (
        <div className="loadingSpinner">
          <CircularProgress />
        </div>
      )}
      <div ref={dicomCanvasRef} className={cornerstoneClasses} />
    </StyleWrapper>
  );
};
